/**
 * The Settlement Assurance Sentinel's Usage slice, as it reads after its recommendation is applied.
 *
 * `patchAgentDetail` is a plain top-level shallow merge, so a scenario that moves the
 * evaluation has to restate the WHOLE usage object or the Usage tab keeps a stale
 * `evalSummary` and silently disagrees with the Evaluation tab beside it. Everything here
 * is the authored bundle verbatim except the two fields that actually move: `evalSummary`
 * (80 → 88, with the dimension the recommendation lifts) and the last point of
 * `evalSpark`. Copied from the bundle rather than retyped, and token-free throughout.
 */
import type { AgentDetailContent } from '@wand/shared/dataset/agent_detail.js';

export const USAGE_SETTLEMENT: AgentDetailContent['usage'] = {
  kpis: [
    {
      icon: 'M3 10h18 M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z',
      ib: 'var(--teal-tint)',
      ic: 'var(--teal)',
      delta: '+0',
      deltaColor: 'var(--ph)',
      value: '412',
      label: 'Releases watched',
      sub: 'this quarter',
    },
    {
      icon: 'M9 18h6 M10 22h4 M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z',
      ib: 'var(--green-tint)',
      ic: 'var(--green)',
      delta: '+3',
      deltaColor: 'var(--green)',
      value: '14',
      label: 'Cases opened',
      sub: 'each at a named person',
    },
    {
      icon: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M6 6l12 12',
      ib: 'var(--red-tint)',
      ic: 'var(--red)',
      delta: '0',
      deltaColor: 'var(--green)',
      value: '0',
      label: 'Held, released or reversed',
      sub: 'treasury decides',
    },
    {
      icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 6v6l4 2',
      ib: 'var(--amber-tint)',
      ic: 'var(--amber-tx)',
      delta: '—',
      deltaColor: 'var(--ph)',
      value: '34s',
      label: 'Shortest authorise to release',
      sub: 'PMT-3242 · SGD 41,900',
    },
  ],
  weekly: [0, 0, 0, 0, 0, 38, 74, 118, 169, 238, 321, 412],
  drivers: [
    {
      label: 'Release-timing reconstruction',
      sub: 'daily',
      count: 620,
      color: 'var(--teal)',
    },
    {
      label: 'Approver-pair census',
      sub: 'weekly',
      count: 372,
      color: 'var(--amber-tx)',
    },
    {
      label: 'Batch-density sweep',
      sub: 'daily',
      count: 248,
      color: 'var(--brand)',
    },
  ],
  usageApps: [
    {
      name: 'FINEOS Claims',
      icon: 'M12 8a8 3 0 1 0 0-6 8 3 0 0 0 0 6z M4 5v6a8 3 0 0 0 16 0V5 M4 11v6a8 3 0 0 0 16 0v-6',
      color: 'var(--teal)',
      calls: 824,
    },
    {
      name: 'Disbursements · Oracle Fusion Financials',
      icon: 'M12 3v18 M5 7h14 M5 7 3 13a3 3 0 0 0 6 0z M19 7l-2 6a3 3 0 0 0 6 0z',
      color: 'var(--green)',
      calls: 372,
    },
    {
      name: 'Policy Ledger',
      icon: 'M8 6h13 M8 12h13 M8 18h13 M3 6h.01 M3 12h.01 M3 18h.01',
      color: 'var(--blue-strong)',
      calls: 96,
    },
  ],
  usageSkills: [
    {
      name: 'Release-timing reconstruction',
      icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 7v5l3 2',
      color: 'var(--teal)',
      bg: 'var(--teal-tint)',
      runs: 620,
    },
    {
      name: 'Approver-pair census',
      icon: 'M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
      color: 'var(--amber-tx)',
      bg: 'var(--amber-tint)',
      runs: 372,
    },
    {
      name: 'Batch-density sweep',
      icon: 'M12 3v18 M5 7h14 M5 7 3 13a3 3 0 0 0 6 0z M19 7l-2 6a3 3 0 0 0 6 0z',
      color: 'var(--blue-strong)',
      bg: 'var(--blue-tint)',
      runs: 248,
    },
    {
      name: 'Probe design & self-validation',
      icon: 'M12 2v2 M5 9h14a1 1 0 0 1 1 1v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a1 1 0 0 1 1-1z M9 14h.01 M15 14h.01 M12 4a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
      color: 'var(--red)',
      bg: 'var(--red-tint)',
      runs: 7,
    },
  ],
  leaders: [
    {
      init: 'MA',
      bg: 'var(--brand-tint)',
      color: 'var(--brand)',
      name: 'Mireille Anand',
      via: 'Claims & Medical Operations',
      count: 8,
    },
    {
      init: 'DC',
      bg: 'var(--green-tint)',
      color: 'var(--green)',
      name: 'Devin Cross',
      via: 'Treasury & Payment Operations',
      count: 4,
    },
    {
      init: 'TV',
      bg: 'var(--teal-tint)',
      color: 'var(--teal)',
      name: 'Tobias Vance',
      via: 'Claims management',
      count: 2,
    },
  ],
  evalSummary: {
    score: '88',
    delta: '+8',
    dims: [
      {
        name: 'Findings carrying both approvers and both timestamps',
        score: '100',
        color: 'var(--green)',
      },
      {
        name: 'Restraint — holds, releases and reverses nothing',
        score: '100',
        color: 'var(--green)',
      },
      {
        name: 'Interval measured from the authorisation event, not the instruction date',
        score: '90',
        color: 'var(--green)',
      },
      {
        name: 'Approver pairs traced across the quarter',
        score: '82',
        color: 'var(--green)',
      },
      {
        name: 'Knows whether the second approver looked at anything before releasing',
        score: '68',
        color: 'var(--green)',
      },
    ],
  },
  evalSpark: [0, 0, 0, 0, 0, 64, 68, 72, 74, 76, 78, 88],
  usersSpark: [0, 0, 0, 0, 0, 1, 1, 2, 2, 3, 4, 4],
  cost: {
    total: '$188',
    perTask: '$0.05',
    perUser: '$47',
    segs: [
      {
        label: 'Model inference',
        amount: '$118',
        value: 118,
        color: 'var(--brand)',
      },
      {
        label: 'FINEOS Claims reads',
        amount: '$44',
        value: 44,
        color: 'var(--teal)',
      },
      {
        label: 'Disbursements reads',
        amount: '$26',
        value: 26,
        color: 'var(--green)',
      },
    ],
  },
  heat: [
    [8, 2, 1, 3, 0, 0],
    [8, 2, 1, 3, 0, 0],
    [8, 2, 1, 3, 0, 0],
    [8, 2, 1, 3, 0, 0],
    [8, 2, 1, 3, 0, 0],
    [6, 0, 0, 0, 0, 0],
    [6, 0, 0, 0, 0, 0],
  ],
};
